/**
 * Shared sim types. No DOM, no rendering. Pure data + logic so the sim can run
 * identically in a browser tab (vs bot) and on a Node server (future MP).
 */

export type Eid = number;

export interface Vec2 {
  x: number;
  y: number;
}

export interface PlayerState {
  id: number;
  civ: string;
  color: string;
  kind: "human" | "bot";
  /** Alliance: players sharing a team are allies (don't fight, share vision, win
   *  together). Distinct teams are enemies. Default = own id (free-for-all). */
  team: number;
  resources: Record<string, number>;
  /** Current population in use. */
  population: number;
  /** Population cap (sum of building-provided capacity + base). */
  populationCap: number;
  alive: boolean;
  defeated: boolean;
}

/** Commands are the ONLY way simulation state changes. Humans and bots both
 *  produce commands; the executor validates ownership and applies them.
 *  For future lockstep MP, the server just relays these between clients. */
export type Command =
  | { type: "move"; player: number; entities: number[]; x: number; y: number; queued?: boolean }
  | { type: "attackMove"; player: number; entities: number[]; x: number; y: number; queued?: boolean }
  | { type: "attack"; player: number; entities: number[]; target: Eid; queued?: boolean }
  | { type: "stop"; player: number; entities: number[] }
  | { type: "gather"; player: number; entities: number[]; target: Eid; queued?: boolean }
  | { type: "returnResources"; player: number; entities: number[]; target: Eid; queued?: boolean }
  | { type: "build"; player: number; workers: number[]; buildingId: string; x: number; y: number; queued?: boolean }
  | { type: "construct"; player: number; workers: number[]; target: Eid; queued?: boolean }
  | { type: "train"; player: number; building: Eid; unitId: string }
  | { type: "cancelTrain"; player: number; building: Eid; index: number }
  | { type: "cancelConstruction"; player: number; building: Eid }
  | { type: "setRally"; player: number; building: Eid; x: number; y: number; resource?: Eid }
  // load selected LAND units into a transport ship
  | { type: "embark"; player: number; entities: number[]; transport: Eid; queued?: boolean }
  // a selected transport disgorges its cargo onto land near (x,y)
  | { type: "unload"; player: number; transport: Eid; x: number; y: number; queued?: boolean };

/** Transient visual events emitted by the sim for the renderer to draw.
 *  Cosmetic only — never affect simulation. */
export type SimEvent =
  | { type: "projectile"; tick: number; from: Vec2; to: Vec2; color: string; kind?: "arrow" | "bolt" | "shell" }
  | { type: "hit"; tick: number; at: Vec2 }
  | { type: "death"; tick: number; at: Vec2 }
  | { type: "spawn"; tick: number; at: Vec2; color: string };

export const FIXED_DT = 1 / 20; // 20 ticks/sec — deterministic fixed timestep
